import type { TaskInputField } from '@/api'

/**
 * The kinds of value a declared input takes, in the order the task form offers them, each in the plain word the form
 * shows (#1754). The stored word is what a skill and the API read; nobody filling in the form sees it.
 */
export const INPUT_TYPE_CHOICES: ReadonlyArray<{ type: TaskInputField['type']; label: string }> = [
    { type: 'string', label: 'Text' },
    { type: 'number', label: 'Number' },
    { type: 'link', label: 'Link' },
    { type: 'boolean', label: 'Yes/no' },
    // A file the task is given: the run-start form offers a picker for it (os#679).
    { type: 'file', label: 'File' },
]

/**
 * The key a skill reads a field by, derived from the name a person gives it (#1754): its letters and digits, in
 * camelCase (`Screen lane label` → `screenLaneLabel`, `Repo (owner/name)` → `repoOwnerName`), the way existing keys
 * read (`planKey`, `maxInFlight`). Empty when the name has no letter or digit, so there is no key to give it.
 */
export function inputKeyOf(name: string): string {
    const words = name.match(/[\p{L}\p{N}]+/gu) ?? []
    return words
        .map((word, index) => {
            const lower = word.toLowerCase()
            return index === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1)
        })
        .join('')
}

/**
 * The `<input>` a typed field is filled in with on the run-start and retry forms: a number field for a number, a URL
 * field for a link (#1754), and a text field for the rest.
 */
export function typedInputType(field: Pick<TaskInputField, 'type'>): 'number' | 'url' | 'text' {
    if (field.type === 'number') {
        return 'number'
    }
    return field.type === 'link' ? 'url' : 'text'
}
